/**
 * 拼音编辑器：**改一个字的读音 / 改一个整词的读音**。
 *
 * ## 为什么这条是"最重要的编辑功能"
 *
 * 听写靠读音驱动孩子写字。多音字读错不是"体验差一点"，而是**直接教错**：
 * 把"长大"读成 cháng dà、把"银行"读成 yín xíng，孩子写出来的字就是错的。
 * 所以这个入口必须**顺手**（列表里点拼音就能编辑），而且必须能**回到自动值**。
 *
 * ## 语义（第一步定下来的，这里只照做）
 *
 * · 输入框里**留空**（或点「恢复自动」）= `{"pinyin": ""}` = **清除人工覆盖**，
 *   回到内置字表的读音。⛔ 不是"把这个字读成空"。
 * · `pinyinAuto` 永远显示出来，家长才知道"自动值是多少、我改成了什么"。
 * · 多音字的备选读音（`pinyinAlternates`，已按最常用在前排序）直接做成候选按钮 ——
 *   家长不用自己去打声调符号。
 *
 * ## 校验
 *
 * 单字：一个音节。
 * 整词：**音节数必须与字数一致**（`validateWordPinyin`，与后端同一个纯函数）——
 * 少一个音节在听写时会**少读一个字**，而界面上完全看不出来，所以必须在保存前拦住。
 * 本地校验只是"少跑一趟"，服务端仍然是防线。
 */
import { useEffect, useMemo, useState } from 'react';
import { validateWordPinyin } from '@fht/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { IconCheck, IconRotateCcw } from '../ui/Icons';
import { cn } from '../../lib/utils';

export interface PinyinEditorProps {
  kind: 'hanzi' | 'word';
  /** 单字或整词。 */
  text: string;
  /** 内置字表算出来的读音（只读展示）。 */
  pinyinAuto: string;
  /** 当前生效的读音。 */
  pinyin: string;
  overridden: boolean;
  /** 多音字备选（只有单字有）。 */
  alternates?: string[];
  saving: boolean;
  error: string | null;
  onSave: (pinyin: string) => void;
  onCancel: () => void;
}

export function PinyinEditor({
  kind,
  text,
  pinyinAuto,
  pinyin,
  overridden,
  alternates = [],
  saving,
  error,
  onSave,
  onCancel,
}: PinyinEditorProps) {
  // 打开时的初值 = 当前生效值；**清空后保存**就是恢复自动
  const [value, setValue] = useState(pinyin);

  // 换了一条记录（text/id 变了）就重置，避免把上一条的输入带过来
  useEffect(() => {
    setValue(pinyin);
  }, [text, pinyin]);

  const trimmed = value.trim();

  /** 本地校验：与后端同一口径（`validateWordPinyin` 来自 @fht/shared）。 */
  const localError = useMemo(() => {
    if (trimmed === '') return null; // 空 = 恢复自动，合法
    if (kind === 'hanzi') {
      return /^[^A-Z0-9\s]+$/.test(trimmed)
        ? null
        : '单个字的拼音只能是一个音节，且只能用小写字母加声调符号（例如 háng）';
    }
    const result = validateWordPinyin(text, trimmed);
    return result.ok ? null : result.reason;
  }, [kind, text, trimmed]);

  // 候选：自动值 + 备选读音 + 当前覆盖值（去重，顺序稳定）
  const candidates = useMemo(() => {
    const all = [pinyinAuto, ...alternates, overridden ? pinyin : ''].filter((v) => v !== '');
    return [...new Set(all)];
  }, [pinyinAuto, alternates, overridden, pinyin]);

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">
            {kind === 'hanzi' ? '改这个字的读音' : '改这个词的读音'}
          </p>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            自动值：<span className="font-mono">{pinyinAuto === '' ? '（内置字表里没有这个字）' : pinyinAuto}</span>
            {overridden && <span className="ml-2 text-amber-700">当前是人工改过的</span>}
          </p>
        </div>
        {/* 按钮列不参与收缩（既有约定），窄屏上让上面的说明文字先让位 */}
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={saving}
            onClick={() => onSave('')}
            icon={<IconRotateCcw size={16} />}
            title="清除人工覆盖，回到内置字表的读音"
          >
            恢复自动
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <Input
            label={`读音（留空也能保存，等于恢复自动）`}
            aria-label={`${text} 的读音`}
            value={value}
            disabled={saving}
            spellCheck={false}
            autoComplete="off"
            placeholder={pinyinAuto !== '' ? pinyinAuto : '例如 háng'}
            onChange={(event) => setValue(event.target.value)}
            error={localError}
            className="font-mono"
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            loading={saving}
            disabled={localError !== null}
            onClick={() => onSave(trimmed)}
            icon={<IconCheck size={16} />}
          >
            保存
          </Button>
          <Button size="sm" variant="secondary" disabled={saving} onClick={onCancel}>
            取消
          </Button>
        </div>
      </div>

      {candidates.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-slate-500">
            {kind === 'hanzi' ? '备选读音（点一下填进输入框）' : '按字拼出来的读音'}
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {candidates.map((candidate) => (
              <button
                key={candidate}
                type="button"
                disabled={saving}
                onClick={() => setValue(candidate)}
                className={cn(
                  'rounded-lg border px-2 py-1 font-mono text-sm',
                  candidate === trimmed
                    ? 'border-brand-400 bg-white text-brand-700'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                )}
              >
                {candidate}
              </button>
            ))}
          </div>
        </div>
      )}

      {error !== null && (
        <p role="alert" className="mt-2 rounded-lg bg-rose-50 px-2 py-1.5 text-sm text-rose-700">
          {error}
        </p>
      )}
    </div>
  );
}
